import { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Bell, Check, CheckCheck, Ticket, FileText, CreditCard, MessageSquare, Loader2 } from 'lucide-react';
import api from '../../services/api';

// Map notification types to icons and colors
const NOTIFICATION_CONFIG = {
  ticket_created: { icon: Ticket, color: 'text-blue-500', bg: 'bg-blue-100 dark:bg-blue-900/50' },
  ticket_reply: { icon: MessageSquare, color: 'text-blue-500', bg: 'bg-blue-100 dark:bg-blue-900/50' },
  ticket_update: { icon: Ticket, color: 'text-blue-500', bg: 'bg-blue-100 dark:bg-blue-900/50' },
  ticket_closed: { icon: Ticket, color: 'text-green-500', bg: 'bg-green-100 dark:bg-green-900/50' },
  invoice_created: { icon: FileText, color: 'text-amber-500', bg: 'bg-amber-100 dark:bg-amber-900/50' },
  invoice_due: { icon: FileText, color: 'text-red-500', bg: 'bg-red-100 dark:bg-red-900/50' },
  payment_received: { icon: CreditCard, color: 'text-green-500', bg: 'bg-green-100 dark:bg-green-900/50' },
  quote_ready: { icon: FileText, color: 'text-purple-500', bg: 'bg-purple-100 dark:bg-purple-900/50' },
  quote_accepted: { icon: Check, color: 'text-green-500', bg: 'bg-green-100 dark:bg-green-900/50' },
  default: { icon: Bell, color: 'text-neutral-500', bg: 'bg-neutral-100 dark:bg-neutral-700' }
};

function getNotificationConfig(type) {
  return NOTIFICATION_CONFIG[type] || NOTIFICATION_CONFIG.default;
}

function getNotificationLink(notification) {
  const { entity_type, entity_id } = notification;
  if (!entity_type || !entity_id) return null;

  switch (entity_type) {
    case 'ticket':
      return `/tickets/${entity_id}`;
    case 'invoice':
      return `/invoices/${entity_id}`;
    case 'quote':
      return `/quotes/${entity_id}`;
    case 'payment':
      return '/payments';
    default:
      return null;
  }
}

function formatTimeAgo(dateString) {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}

export default function NotificationDropdown() {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const queryClient = useQueryClient();

  // Fetch unread count
  const { data: countData, error: countError } = useQuery({
    queryKey: ['notification-count'],
    queryFn: async () => {
      const response = await api.get('/notifications/count');
      return response.data;
    },
    refetchInterval: 60000, // Refetch every minute
    retry: false,
  });

  // Log error if count fetch fails
  if (countError) {
    console.error('Failed to fetch notification count:', countError);
  }

  // Fetch notifications when dropdown is open
  const { data: notificationsData, isLoading } = useQuery({
    queryKey: ['notifications'],
    queryFn: async () => {
      const response = await api.get('/notifications?limit=10');
      return response.data;
    },
    enabled: isOpen,
  });

  // Mark as read mutation
  const markAsReadMutation = useMutation({
    mutationFn: async (notificationId) => {
      await api.patch(`/notifications/${notificationId}/read`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: ['notification-count'] });
    }
  });

  // Mark all as read mutation
  const markAllAsReadMutation = useMutation({
    mutationFn: async () => {
      await api.post('/notifications/read-all');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: ['notification-count'] });
    }
  });

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const unreadCount = countData?.count || 0;
  const notifications = notificationsData?.notifications || [];

  const handleNotificationClick = (notification) => {
    if (!notification.is_read) {
      markAsReadMutation.mutate(notification.id);
    }
    setIsOpen(false);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="p-2 text-neutral-500 hover:text-neutral-700 dark:text-neutral-400 dark:hover:text-neutral-200 relative"
      >
        <Bell className="w-5 h-5" />
        {unreadCount > 0 && (
          <span className="absolute top-1 right-1 min-w-[18px] h-[18px] bg-primary-500 text-white text-xs font-medium rounded-full flex items-center justify-center px-1">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-80 sm:w-96 bg-white dark:bg-neutral-800 rounded-lg shadow-lg border border-neutral-200 dark:border-neutral-700 z-50 overflow-hidden">
          {/* Header */}
          <div className="px-4 py-3 border-b border-neutral-200 dark:border-neutral-700 flex items-center justify-between">
            <h3 className="font-semibold text-neutral-900 dark:text-white">Notifications</h3>
            {unreadCount > 0 && (
              <button
                onClick={() => markAllAsReadMutation.mutate()}
                disabled={markAllAsReadMutation.isPending}
                className="text-xs text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 flex items-center gap-1"
              >
                {markAllAsReadMutation.isPending ? (
                  <Loader2 className="w-3 h-3 animate-spin" />
                ) : (
                  <CheckCheck className="w-3 h-3" />
                )}
                Mark all read
              </button>
            )}
          </div>

          {/* Notifications List */}
          <div className="max-h-96 overflow-y-auto">
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-primary-500" />
              </div>
            ) : notifications.length === 0 ? (
              <div className="py-8 text-center">
                <Bell className="w-8 h-8 mx-auto mb-2 text-neutral-300 dark:text-neutral-600" />
                <p className="text-sm text-neutral-500 dark:text-neutral-400">No notifications yet</p>
              </div>
            ) : (
              <div className="divide-y divide-neutral-100 dark:divide-neutral-700">
                {notifications.map((notification) => {
                  const config = getNotificationConfig(notification.type);
                  const Icon = config.icon;
                  const link = getNotificationLink(notification);

                  const content = (
                    <div
                      className={`px-4 py-3 hover:bg-neutral-50 dark:hover:bg-neutral-700/50 transition-colors cursor-pointer ${
                        !notification.is_read ? 'bg-primary-50/50 dark:bg-primary-900/20' : ''
                      }`}
                      onClick={() => handleNotificationClick(notification)}
                    >
                      <div className="flex gap-3">
                        <div className={`flex-shrink-0 w-8 h-8 rounded-full ${config.bg} flex items-center justify-center`}>
                          <Icon className={`w-4 h-4 ${config.color}`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm ${!notification.is_read ? 'font-medium' : ''} text-neutral-900 dark:text-white`}>
                            {notification.title}
                          </p>
                          {notification.message && (
                            <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5 line-clamp-2">
                              {notification.message}
                            </p>
                          )}
                          <p className="text-xs text-neutral-400 dark:text-neutral-500 mt-1">
                            {formatTimeAgo(notification.created_at)}
                          </p>
                        </div>
                        {!notification.is_read && (
                          <div className="flex-shrink-0">
                            <span className="w-2 h-2 bg-primary-500 rounded-full block" />
                          </div>
                        )}
                      </div>
                    </div>
                  );

                  return link ? (
                    <Link key={notification.id} to={link} onClick={() => handleNotificationClick(notification)}>
                      {content}
                    </Link>
                  ) : (
                    <div key={notification.id}>{content}</div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Footer */}
          {notifications.length > 0 && (
            <div className="px-4 py-2 border-t border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-700/50">
              <button
                onClick={() => setIsOpen(false)}
                className="text-xs text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-200 w-full text-center"
              >
                Close
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
